import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Label,
  Input,
  FormFeedback,
  Spinner
} from "reactstrap";
import classnames from "classnames"; // for toggling active tab classes
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import axiosInstance from 'utils/axiosInstance';

// Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const InfoInput = ({ t }) => {

  document.title = "我的資料管理 | TheChart.AI";

  const [activeTab, setActiveTab] = useState("1");

  // Toggle active tab
  const toggleTab = (tab) => {
    if (activeTab !== tab) { setActiveTab(tab); }
  };


  const [infoQuestions, setInfoQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // Store user inputs
  const [verifiedAnswers, setVerifiedAnswers] = useState({}); // Track verified answers
  const [ruleVerificationStatus, setRuleVerificationStatus] = useState({}); 
  const [confirmedAnswers, setConfirmedAnswers] = useState({}); // Store confirmed inputs
  const [lastVerifiedAnswers, setLastVerifiedAnswers] = useState({}); // Store last verified answers

  const [answerAwaitSaving, setAnswerAwaitSaving] = useState("");

  const fetchInfoData = async () => {
    try {
      
      const questionsRes = await axiosInstance.get("/knovia/info/infoQuestions");
      const questions = questionsRes.data.questionArray;
      setInfoQuestions(questions);
      console.log('questions:', questions);

      // Fetch answers
      const answersRes = await axiosInstance.get(`/knovia/info/infoAnswers`);
      const userAnswers = answersRes.data.answers; // Expecting { questionId: answer }

      // Initialize answers state
      const initialAnswers = {};
      const initialVerificationStatus = {};
      const initialVerifiedAnswers = {};
      const initialConfirmedAnswers = {};

      questions.forEach((q) => {
        initialAnswers[q._id] = userAnswers[q._id]?.answer || ""; // Populate existing answers or default ""

        if(userAnswers[q._id]?.answer.trim().length){
          initialVerifiedAnswers[q._id] = userAnswers[q._id]?.isAIVerified || false;
          initialConfirmedAnswers[q._id] = userAnswers[q._id]?.answer || "";
          initialVerificationStatus[q._id] = q.verifyRuleAI?.map(() => "YES") || [];
        }

      });

      setAnswers(initialAnswers);
      setVerifiedAnswers(initialVerifiedAnswers);
      setRuleVerificationStatus(initialVerificationStatus);
      setConfirmedAnswers(initialConfirmedAnswers);

      console.log("Fetched user answers:", userAnswers);
    } catch (err) {
      console.error("Error fetching data", err);
    }
  }

  // Load from server
  useEffect(() => {
    fetchInfoData();
  }, [t]);


  useEffect(()=>{
    console.log('infoQuestions changed:', infoQuestions);
  },[infoQuestions])

  // Handle input change
  const handleInputChange = (questionId, event) => {
    const newAnswer = event.target.value;

    setAnswers((prev) => ({
      ...prev,
      [questionId]: newAnswer,
    }));

    // Reset verification status and confirmed answers when input changes
    setVerifiedAnswers((prev) => ({
      ...prev,
      [questionId]: false, // Reset verification status
    }));

    setRuleVerificationStatus((prev) => ({
      ...prev,
      [questionId]: undefined, // Reset rule colors
    }));

    setConfirmedAnswers((prev) => ({
      ...prev,
      [questionId]: undefined, // Reset confirmed answers
    }));
  };


  // Handle verification (檢驗)
  const handleVerify = async (questionId) => {
    const answer = answers[questionId];

    if (!answer.trim()) return; // Prevent verifying empty answers

    // Prevent redundant verification if the answer hasn't changed
    if (lastVerifiedAnswers[questionId] === answer.trim()) {
      console.log(`Answer for question ${questionId} has not changed, skipping verification.`);
      return;
    }

    try {
      const res = await axiosInstance.post("/knovia/info/verifyAnswer", {
        questionId,
        answer,
      });

      const { isValid, results } = res.data;

      // Store per-rule verification results
      setRuleVerificationStatus((prev) => ({
        ...prev,
        [questionId]: results, // Array of "YES" or "NO" for each rule
      }));

      setLastVerifiedAnswers((prev) => ({
        ...prev,
        [questionId]: answer.trim(), // Store the verified answer
      }));

      if (isValid) {
        setVerifiedAnswers((prev) => ({
          ...prev,
          [questionId]: true,
        }));
        setConfirmedAnswers((prev) => ({
          ...prev,
          [questionId]: undefined, // Reset confirmed answers
        }));
        console.log(`Answer verified for question ${questionId}.`);
      } else {
        console.log(`Verification failed:`, results);
      }
    } catch (error) {
      console.error("Error verifying answer", error);
    }
  };


  // Handle saving (儲存)
  const handleSave = async (questionId) => {
    if (!verifiedAnswers[questionId]) return; // Prevent saving if not verified
    setAnswerAwaitSaving(prev => questionId);

    const answer = answers[questionId];

    try {
      const updateResult = await axiosInstance.post("/knovia/info/updateAnswer", {
        questionId,
        answer,
      });

      const { message, hasAttributesAdded } = updateResult.data;

      console.log('hasAttributesAdded:', hasAttributesAdded);

      // Fetch Question Array again
      if(hasAttributesAdded){
        console.log('fetching infoData...');
        fetchInfoData();
      }

      setConfirmedAnswers((prev) => ({
        ...prev,
        [questionId]: answer,
      }));

      setAnswerAwaitSaving(prev => "");
      console.log(`Saved answer for question ${questionId}:`, answer);
    } catch (error) {
      setAnswerAwaitSaving(prev => "");
      console.error("Error saving answer", error);
    }
  };



  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs
            title={t("個人化 AI 應用")}
            breadcrumbItem={t("我的資料管理")}
          />

          <Card>
            <CardBody>
              <CardTitle className="mb-4">{t("我的資料管理")}</CardTitle>

              {/* Navigation Tabs */}
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => toggleTab("1")}
                  >
                    {t("基本資料")}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => toggleTab("2")}
                  >
                    {t("聯絡方式")}
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="pt-4">
                {/* Tab 1: Basic Info */}
                <TabPane tabId="1">
                  <Row>
                    {infoQuestions.map((infoQuestion) => (
                      <Col md="12" key={infoQuestion._id} className="mb-4">
                        <Row style={{ display:"flex" }}>
                          <Col md="4">
                            <Label className="form-label" style={{ margin: "0.3rem 0rem 0.5rem 0rem" }}>
                              {infoQuestion.label}
                            </Label>
                          </Col>
                          <Col md="6" xs="8">

                            <Row style={{ display: "flex", flexDirection: "column" }}>
                              <Col>
                                <Input
                                  type="text"
                                  value={answers[infoQuestion._id] || ""}
                                  onChange={(e) =>
                                    handleInputChange(infoQuestion._id, e)
                                  }
                                />
                              </Col>
                              <Col>
                                {infoQuestion?.verifyRuleAI.map((verifyRule, index) => {
                                  let color = "#9b9b9b"; // Default grey for unverified
                                  if (ruleVerificationStatus[infoQuestion._id]) {
                                    color = ruleVerificationStatus[infoQuestion._id][index] === "YES" ? "green" : "red";
                                  }

                                  return (
                                    <p key={index} style={{ margin: "0", fontSize: "11px", color }}>
                                      {verifyRule}
                                    </p>
                                  );
                                })}
                              </Col>
                            </Row>

                          </Col>
                          <Col md="2" xs="4">
                            <Button
                              color={verifiedAnswers[infoQuestion._id] ? "success" : "warning"}
                              style={{ marginRight: "0.3rem", fontSize: "20px", padding: "0.1rem 0.4rem" }}
                              onClick={() => handleVerify(infoQuestion._id)}
                              disabled={
                                verifiedAnswers[infoQuestion._id] || // Disable after verification
                                lastVerifiedAnswers[infoQuestion._id] === answers[infoQuestion._id] // Disable if answer hasn’t changed
                              }
                            >
                              <i className={`bx ${verifiedAnswers[infoQuestion._id] ? "bx-list-check" : "bx-list-check"}`}></i>
                            </Button>

                            <Button
                              color={confirmedAnswers[infoQuestion._id] ? "success" : "primary"}
                              style={{ fontSize: "20px", padding: "0.1rem 0.4rem" }}
                              onClick={() => handleSave(infoQuestion._id)}
                              disabled={
                                !verifiedAnswers[infoQuestion._id] || 
                                !!confirmedAnswers[infoQuestion._id] ||
                                answerAwaitSaving===infoQuestion._id
                              } // Only enable if verified
                            >
                              {answerAwaitSaving===infoQuestion._id ? (
                                <div style={{ fontSize: "6px", padding: "0.38rem 0.13rem" }}>
                                  <Spinner style={{
                                    color: "white",
                                    height: "16px",
                                    width: "16px",
                                    margin: "0"
                                  }}/>
                                </div>
                              ) : (
                                <i className={`bx ${confirmedAnswers[infoQuestion._id] ? "bx-check-circle" : "bx-check-circle"}`}></i>
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    ))}
                  </Row>
                </TabPane>

                {/* Tab 2: Contact Info */}
                <TabPane tabId="2">
                  <Row>
                    {/*
                    {fields.contactInfo.map((field, index) => (
                      <Col md="12" key={field.id} className="mb-4">
                        <Row>
                          <Col md="4">
                            <Label className="form-label">{field.label}</Label>
                          </Col>
                          <Col md="8">
                            <Input
                              type="text"
                              value={field.value}
                              onChange={(e) => handleInputChange("contactInfo", index, e)}
                              invalid={!!field.error}
                            />
                            {field.error && (
                              <FormFeedback>{field.error}</FormFeedback>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    ))}
                    */}
                  </Row>
                </TabPane>
              </TabContent>

              {/* Save Button */}
              {/*
              <Row className="mt-4">
                <Col>
                  <Button color="primary" onClick={handleSave}>
                    {t("儲存")}
                  </Button>
                </Col>
              </Row>
              */}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

InfoInput.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(InfoInput);
